/* eslint-disable prettier/prettier */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { Formik, FormikValues, Form } from 'formik';
import { RootState } from 'store';
import * as yup from 'yup';

import Field from 'components/Field';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useUserAccount from 'hooks/useUserAccount';
import { CostCenter } from 'models/costCenter';
import { ProductType } from 'models/enums/productType';
import { HotelDetailsCompleteResponse, RoomRatePrice } from 'models/hotelDetailsComplete';
import ConfirmProfile from 'screens/components/Booking/ConfirmProfile';
import TravelJustify from 'screens/components/TravelJustify';
import { getCostCenters } from 'services/costCenterService';
import { BreakRulesReason } from 'services/travelBreakRulesReasonService';
import { Reason } from 'services/travelReasonService';

import Rooms from '../Rooms';

interface RoomForm {
  justifyForBreakingRules: string;
  reason?: Reason;
  roomSelected: {
    roomRateId: string;
    roomRate: { refundableType: string };
    breakRulesReasonMessage?: string;
  };
  costCenter?: Reason;
  breakRulesReason?: BreakRulesReason;
}

export interface Props {
  creating: boolean;
  details: HotelDetailsCompleteResponse;
  bookingDisabled?: boolean;
  onConfirm: (
    justifyForBreakingRules: string,
    reason: string,
    roomRateId: string,
    roomRate: { refundableType: string },
    roomRatePrice: RoomRatePrice,
    costCenterId?: string,
    breakRulesReasonId?: string,
  ) => void;
  setShowConfirmProfile: (value: boolean) => void;
  showConfirmProfile: boolean;
  setShowTravelJustify: (value: boolean) => void;
  showTravelJustify: boolean;
}

const RoomList = ({
  creating,
  details,
  onConfirm,
  bookingDisabled = true,
  setShowConfirmProfile,
  showConfirmProfile,
  setShowTravelJustify,
  showTravelJustify,
}: Props) => {
  const { t } = useStoreTranslation();
  const userAccount = useUserAccount();
  const { traveler } = useSelector((state: RootState) => state.hotels);
  const [costCenters, setCostCenters] = useState<CostCenter[]>([]);

  const getRoomPrice = useCallback(
    (roomRateId: string) => {
      const room = details ? details.rooms.find((item) => item.roomRate.rateId === roomRateId) : undefined;

      if (room) {
        return room?.roomRate.price;
      }
      return undefined;
    },
    [details],
  );

  const getBreakRulesMessage = useCallback(
    (roomRateId: string) => {
      const room = details ? details.rooms.find((item) => item.roomRate.rateId === roomRateId) : undefined;

      if (room) {
        return room?.roomRate.price.breakRulesMessage;
      }
      return undefined;
    },
    [details],
  );

  const initialValues: RoomForm = useMemo(
    () =>
      costCenters && costCenters.length > 0
        ? {
          justifyForBreakingRules: '',
          reason: undefined,
          roomSelected: {
            roomRateId: details ? details.rooms.first().roomRate.rateId : '',
            roomRate: { refundableType: '' },
            breakRulesReasonMessage: undefined
          },
          costCenter: undefined,
          breakRulesReason: undefined
        }
        : {
          justifyForBreakingRules: '',
          reason: undefined,
          roomSelected: {
            roomRateId: details ? details.rooms.first().roomRate.rateId : '',
            roomRate: { refundableType: '' },
            breakRulesReasonMessage: undefined
          },
          breakRulesReason: undefined
        },
    [costCenters, details],
  );

  useEffect(() => {
    getCostCenters().then((res) => {
      if (res && res.length > 0) {
        const centers = res.filter((c) => c.isActive);
        if (centers.length > 0) setCostCenters(centers);
      }
    });
  }, [t]);

  const handleSubmit = ({ justifyForBreakingRules, reason, roomSelected, costCenter, breakRulesReason }: RoomForm) => {
    const roomRatePrice = getRoomPrice(roomSelected.roomRateId);

    if (!roomRatePrice) return;
    if (!reason) return;

    onConfirm(
      justifyForBreakingRules,
      reason.id,
      roomSelected.roomRateId,
      roomSelected.roomRate,
      roomRatePrice,
      costCenter?.id,
      breakRulesReason?.id
    );
  };

  const validationSchema = useMemo(
    () =>
      costCenters && costCenters.length > 0
        ? yup.object().shape({
          justifyForBreakingRules: yup.string().min(10).max(300).trim().required(),
          reason: yup.object().required(),
          roomSelected: yup.object().shape({
            roomRateId: yup.string().required(),
            romRate: yup
              .object()
              .shape({
                refundableType: yup.string(),
              })
              .required(),
          }),
          costCenter: yup.object().required(),
        })
        : yup.object().shape({
          justifyForBreakingRules: yup.string().min(10).max(300).trim().required(),
          reason: yup.object().required(),
          roomSelected: yup.object().shape({
            roomRateId: yup.string().required(),
            romRate: yup
              .object()
              .shape({
                refundableType: yup.string(),
              })
              .required(),
          }),
        }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [costCenters],
  );

  if (!userAccount) return null;

  return (
    <>
      <ConfirmProfile
        show={showConfirmProfile}
        productType={ProductType.Hotel}
        onClose={() => setShowConfirmProfile(false)}
        onConfirm={() => setShowTravelJustify(true)}
        user={traveler ? { ...traveler, nationality: traveler.country, userId: traveler.id || traveler.userId } : { userId: userAccount.id, ...userAccount }}
      />

      <Formik
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
        isInitialValid={(formik: FormikValues) => validationSchema.isValidSync(formik.initialValues)}
      >
        {({ values, isValid, handleSubmit: submit }: FormikValues) => (
          <Form>
            <Field name="roomSelected">
              <Rooms
                details={details}
                rooms={details.rooms}
                optionId={details.optionId}
                searchId={details.searchId}
                visibility={() => setShowConfirmProfile(true)}
                checkinDate={details.checkinDate}
                checkoutDate={details.checkoutDate}
                currencyCode={details.currencyCode}
                bookingDisabled={bookingDisabled}
              />
            </Field>

            <TravelJustify
              show={showTravelJustify}
              onClose={() => setShowTravelJustify(false)}
              itineraryValue={getRoomPrice(values.roomSelected.roomRateId)?.total}
              currencyCode={getRoomPrice(values.roomSelected.roomRateId)?.currencyCode}
              isValid={isValid}
              loading={creating}
              onSubmit={submit}
              productType={ProductType.Hotel}
              costCenters={costCenters}
              breakRulesMessage={getBreakRulesMessage(values.roomSelected.roomRateId)}
            />
          </Form>
        )}
      </Formik>
    </>
  );
};

export default RoomList;
